import { Component, OnInit } from "@angular/core";
import { FlightsService } from "../flights-calendar/flights.service";
import moment from "moment";
import { DatePipe } from "@angular/common";
import { ExcelExportEvent } from "@progress/kendo-angular-grid";
import { HHMM } from "@rrsolutionsit/atl-utils";

@Component({
    selector: "app-pilot-activity",
    standalone: false,
    templateUrl: "./pilot-activity.component.html",
    styleUrl: "./pilot-activity.component.scss",
})
export class PilotActivityComponent implements OnInit {
    public selectedDate: Date;
    public pilotActivityList = [];
    public pilotList = [];
    public today = new Date();

    constructor(
        private _flightsService: FlightsService,
        private _datePipe: DatePipe,
    ) {}

    ngOnInit(): void {
        this.selectedDate = new Date();
        this.onDateChange(this.selectedDate);
    }

    public onDateChange(date: Date): void {
        const firstDay: Date = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay: Date = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        this._flightsService.getHelicopterFlights(moment(firstDay).format("YYYY-MM-DD"), moment(lastDay).format("YYYY-MM-DD"), null).subscribe((flights) => {
            this.pilotList = [];

            flights.forEach((flight) => {
                if (flight.pilot1Name && flight.pilot1Name !== null && !this.pilotList.find((pilot) => pilot.name === flight.pilot1Name)) {
                    this.pilotList.push({ id: "_" + this.pilotList.length, name: flight.pilot1Name });
                }
                if (flight.pilot2Name && flight.pilot2Name !== null && !this.pilotList.find((pilot) => pilot.name === flight.pilot2Name)) {
                    this.pilotList.push({ id: "_" + this.pilotList.length, name: flight.pilot2Name });
                }
            });

            this.pilotList.sort((a, b) => {
                const nameA = a.name.trim().split(" ").pop().toLowerCase();
                const nameB = b.name.trim().split(" ").pop().toLowerCase();

                return nameA.localeCompare(nameB);
            });

            this.pilotActivityList = [];
            const currentDate = new Date(firstDay);

            while (currentDate <= lastDay) {
                const activityEntries = new Map();
                activityEntries.set("day", this._datePipe.transform(currentDate, "EEE"));
                activityEntries.set("date", moment(currentDate).format("DD/MM/YYYY"));

                flights
                    .filter((flight) => moment(flight.flightDate).isSame(moment(currentDate), "day"))
                    .forEach((flight) => {
                        if (activityEntries.has(flight.pilot1Name)) {
                            activityEntries.get(flight.pilot1Name).daily = HHMM.sumTimes(activityEntries.get(flight.pilot1Name).daily, flight.flightTime).value;
                        } else if (flight.pilot1Name && flight.pilot1Name !== null) {
                            activityEntries.set(flight.pilot1Name, { daily: flight.flightTime });
                        }

                        if (activityEntries.has(flight.pilot2Name)) {
                            activityEntries.get(flight.pilot2Name).daily = HHMM.sumTimes(activityEntries.get(flight.pilot2Name).daily, flight.flightTime).value;
                        } else if (flight.pilot2Name && flight.pilot2Name !== null) {
                            activityEntries.set(flight.pilot2Name, { daily: flight.flightTime });
                        }
                    });

                if (currentDate.getDay() === 0) {
                    this.pilotActivityList.forEach((entry) => {
                        this.pilotList.forEach((pilot) => {
                            if (entry[pilot.name]) {
                                activityEntries.set(pilot.name, { weekly: HHMM.sumTimes(activityEntries.get(pilot.name)?.weekly || "00:00", entry[pilot.name].daily).value });
                                activityEntries.set(pilot.name, { weekly: HHMM.subtractTimes(activityEntries.get(pilot.name)?.weekly, entry[pilot.name].weekly).value });
                            }
                        });
                    });
                    this.pilotList.forEach((pilot) => {
                        if (!activityEntries.has(pilot.name)) {
                            activityEntries.set(pilot.name, { weekly: "00:00" });
                        }
                    });
                }

                this.pilotActivityList.push(Object.fromEntries(activityEntries));

                currentDate.setDate(currentDate.getDate() + 1);
            }

            // if there are no pilots clear the whole list so the grid shows the no data message
            if (this.pilotList.length <= 0) {
                this.pilotActivityList = [];
            }
        });
    }

    public getFileName(): string {
        return "Pilot_Activity_" + moment(this.selectedDate).format("MMMM_YYYY") + ".xlsx";
    }

    onExcelExport(e: ExcelExportEvent) {
        const rows = e.workbook.sheets[0].rows;

        // set alternating row color
        let altIdx = 0;
        rows.forEach((row) => {
            if (row.type === "data") {
                if (altIdx % 2 !== 0) row.cells.forEach((cell) => (cell.background = "#f2f7ff"));
                altIdx++;
                row.cells.forEach((cell) => {
                    if (cell.value?.includes(":")) {
                        const parts = cell.value.split(":").map((p) => parseInt(p.trim(), 10));

                        const hours = parts[0];
                        const minutes = parts[1];

                        cell.value = (hours * 3600 + minutes * 60) / (24 * 60 * 60);
                        cell.format = "[h]:mm";
                    }
                });
            }
        });

        const footerRowIndex = rows.findIndex((row) => row.type === "footer");
        const baseFooter = rows[footerRowIndex];

        baseFooter.cells.forEach((cell, index) => {
            if (cell.value) {
                cell.color = "#ffffff";
                cell.background = "#4050b5";
                cell.textAlign = "center";
            } else {
                if (index === 0) {
                    cell.value = "Totals (Days/Hours):";
                    cell.color = "#ffffff";
                }
                cell.background = "#4050b5";
            }
        });
    }

    public sumHours(field: string): string {
        let total = "00:00";
        this.pilotActivityList.forEach((row) => {
            if (row[field]) {
                total = HHMM.sumTimes(total, row[field].daily).value;
            }
        });
        return total;
    }

    public sumOccurences(field: string): number {
        let total = 0;
        this.pilotActivityList.forEach((row) => {
            if (row[field] && row[field].daily) {
                total++;
            }
        });
        return total;
    }
}
